import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getEarnNudgesApi } from '@flipster/apis/v1/earn';
import { atomWithBigNumber } from '@flipster/react/misc/state/atom-with-bignumber';
import { NrsusdtpCurrency, PusdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

export const earnNudgesBunja = bunja(() => {
  const getEarnNudgesApiAtom = atomWithQuery(() => ({
    queryKey: ['getEarnNudgesApi'],
    queryFn: getEarnNudgesApi,
    select: (res) => res.data,
    refetchOnMount: 'always',
  }));

  const earnNudgesAtom = atom((get) => get(getEarnNudgesApiAtom).data);
  const isPendingAtom = atom((get) => get(getEarnNudgesApiAtom).isPending);

  const pusdtMaxAprAtom = atomWithBigNumber((get) => {
    const nudges = get(earnNudgesAtom);
    const maxApr = nudges?.premiumEarn[PusdtCurrency]?.maxAchievableApr;
    return maxApr ? Num(maxApr) : undefined;
  });

  const dynamicEarnEstimatedAprAtom = atomWithBigNumber((get) => {
    const nudges = get(earnNudgesAtom);
    const entries = nudges?.dynamicEarn;
    if (!entries) return undefined;
    const sum = Object.values(entries).reduce((acc, entry) => {
      const apr = entry?.estimatedApr;
      return apr ? acc.plus(apr) : acc;
    }, Num(0));
    return sum.isZero() ? undefined : sum;
  });

  const narsilEstimatedAprAtom = atomWithBigNumber((get) => {
    const nudges = get(earnNudgesAtom);
    const estimatedApr = nudges?.dynamicEarn[NrsusdtpCurrency]?.estimatedApr;
    return estimatedApr ? Num(estimatedApr) : undefined;
  });

  const isDynamicEarnTermsAgreedAtom = atom((get) => {
    const nudges = get(earnNudgesAtom);
    return nudges?.dynamicEarn[NrsusdtpCurrency]?.isTermsAgreed ?? false;
  });

  // 진입 시 refetch(refetchOnMount: 'always')가 끝나기 전까지는 캐시된 이전 응답이 보인다.
  // 그 값으로 nudge를 먼저 그리면 구독 직후 재진입 시 잠깐 떴다가 사라지므로,
  // 응답이 도착한 뒤에만 노출한다. 조회에 실패해 데이터가 없을 때도 노출하지 않는다.
  const isDynamicEarnNudgeVisibleAtom = atom((get) => {
    const { isFetching, data } = get(getEarnNudgesApiAtom);
    const hasResponse = !isFetching && data != null;
    return hasResponse && !get(isDynamicEarnTermsAgreedAtom);
  });

  return {
    getEarnNudgesApiAtom,
    earnNudgesAtom,
    isPendingAtom,
    pusdtMaxAprAtom,
    dynamicEarnEstimatedAprAtom,
    narsilEstimatedAprAtom,
    isDynamicEarnNudgeVisibleAtom,
  };
});
